/**
 * Filtre la galerie produit par couleur.
 *
 * Principe : on filtre AVANT que le carrousel se monte, jamais après.
 *
 * À chaque changement de variante, le thème re-télécharge la page et remplace son
 * <slider-component> par celui qu'il trouve dans event.detail.data.html — un document
 * détaché, en mémoire (product-media-gallery.js). On écoute variant:updated en phase de
 * CAPTURE sur document : l'ordre des phases DOM garantit qu'on passe avant le
 * gestionnaire du thème, sans dépendre de l'ordre de chargement des scripts. On retire
 * alors les photos non voulues de ce document détaché. La page vivante n'est jamais
 * touchée : le thème y insère une galerie déjà filtrée, et Splide se monte sur une liste
 * correcte. Flèches, miniatures, synchronisation et pagination restent natives.
 *
 * Pourquoi pas un masquage en CSS : Splide ne calcule pas ses positions, il les mesure
 * (assets/splide.js:854, :858, :863, et le déplacement lui-même :972). Une diapositive
 * en display:none renvoie un rectangle à zéro, ce qui fausse toutes les positions
 * suivantes — miniatures inertes et flèches mortes.
 *
 * Sécurité : au moindre doute (compte inattendu, structure absente, exception), on ne
 * touche à rien et le thème affiche sa galerie complète. Une galerie non filtrée est un
 * défaut acceptable ; une galerie cassée, non.
 *
 * Limite connue : le premier chargement vient du serveur, complet. Le filtrage démarre
 * au premier changement de couleur.
 */

/** Normalise une valeur d'option pour comparer « Rosé doré » et « rose-dore ». */
const normaliser = (valeur) =>
  (valeur == null ? '' : String(valeur))
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

class GbGalerieCouleur extends HTMLElement {
  connectedCallback() {
    const source = this.querySelector('script[type="application/json"]');
    if (!source) return;

    try {
      this.donnees = JSON.parse(source.textContent);
    } catch (erreur) {
      console.warn('[gb-galerie-couleur] données illisibles, filtrage inactif', erreur);
      return;
    }

    if (!this.donnees || !this.donnees.actif) return;
    if (!Array.isArray(this.donnees.medias) || this.donnees.medias.length === 0) return;
    if (typeof this.donnees.indexOption !== 'number' || this.donnees.indexOption < 0) return;

    this.section = this.closest('.shopify-section');
    if (!this.section) return;

    // Ordre médiathèque, et couleur de groupe de chaque média.
    this.bibliotheque = this.donnees.medias.map((media) => String(media.id));
    this.couleurs = new Map(this.donnees.medias.map((media) => [String(media.id), normaliser(media.c)]));

    this.surVariante = this.surVariante.bind(this);
    document.addEventListener('variant:updated', this.surVariante, true);
  }

  disconnectedCallback() {
    if (this.surVariante) document.removeEventListener('variant:updated', this.surVariante, true);
  }

  surVariante(evenement) {
    try {
      const detail = evenement.detail;
      const variante = detail && detail.resource;
      const documentDetache = detail && detail.data && detail.data.html;
      if (!variante || !documentDetache || typeof documentDetache.querySelector !== 'function') return;

      // Ne réagir qu'aux variantes de notre propre section.
      const origine = evenement.target;
      if (!(origine instanceof Node) || !this.section.contains(origine)) return;

      const couleur = normaliser((variante.options || [])[this.donnees.indexOption]);
      if (!couleur) return;

      const idSelectionne = variante.featured_media ? String(variante.featured_media.id) : '';

      this.elaguerGalerie(documentDetache, couleur, idSelectionne);
      this.remplacerZoom(documentDetache, couleur);
    } catch (erreur) {
      console.warn('[gb-galerie-couleur] filtrage abandonné, la galerie reste complète', erreur);
    }
  }

  /** Ordre d'affichage du thème : la photo de la variante en tête, puis la médiathèque. */
  ordreRendu(idSelectionne) {
    if (!idSelectionne || !this.bibliotheque.includes(idSelectionne)) return this.bibliotheque.slice();
    return [idSelectionne, ...this.bibliotheque.filter((autre) => autre !== idSelectionne)];
  }

  /** Vrai si ce média doit rester : média commun, ou média de la couleur choisie. */
  garder(identifiant, couleur) {
    const groupe = this.couleurs.get(String(identifiant));
    if (groupe === undefined) return true;
    return groupe === '' || groupe === couleur;
  }

  /**
   * Retire les photos non voulues du <slider-component> présent dans le document
   * détaché, avant que le thème ne l'insère dans la page.
   */
  elaguerGalerie(documentDetache, couleur, idSelectionne) {
    const galerie = documentDetache.querySelector('product-media-gallery');
    if (!galerie) return;

    const listePrincipale = galerie.querySelector('[data-ref="main-slider"] .splide__list');
    if (!listePrincipale) return;

    const principales = Array.from(listePrincipale.children);
    const ordre = this.ordreRendu(idSelectionne);

    // Le thème n'identifie pas ses médias : la correspondance est positionnelle.
    // Si le compte n'est pas celui attendu, on renonce plutôt que de deviner.
    if (principales.length !== ordre.length) return;

    const aGarder = ordre.map((identifiant) => this.garder(identifiant, couleur));
    if (aGarder.every(Boolean)) return; // rien à retirer
    if (!aGarder.some(Boolean)) return; // sécurité : ne jamais vider la galerie

    const listeMiniatures = galerie.querySelector('[data-ref="thumbnail-slider"] .splide__list');
    const miniatures = listeMiniatures ? Array.from(listeMiniatures.children) : [];
    const miniaturesAlignees = miniatures.length === ordre.length;

    // Le thème place les badges et la pleine largeur dans la première diapositive :
    // on les transfère si celle-ci disparaît.
    const premierGarde = aGarder.indexOf(true);
    if (premierGarde > 0) {
      const ancienne = principales[0];
      const nouvelle = principales[premierGarde];
      const badges = ancienne.querySelector('.product-badges');
      if (badges) nouvelle.appendChild(badges);
      if (ancienne.classList.contains('product-media-gallery__media--full-width')) {
        nouvelle.classList.add('product-media-gallery__media--full-width');
      }
    }

    for (let rang = ordre.length - 1; rang >= 0; rang -= 1) {
      if (aGarder[rang]) continue;
      principales[rang].remove();
      if (miniaturesAlignees) miniatures[rang].remove();
    }
  }

  /**
   * La vue agrandie vit en dehors du <slider-component> : le thème ne la remplace pas.
   * On lui substitue donc celle du document détaché, élaguée, une fois que le thème a
   * fini son propre remplacement. En cas de doute, on laisse la vue agrandie complète.
   */
  remplacerZoom(documentDetache, couleur) {
    const zoomActuel = this.section.querySelector('product-zoom');
    if (!zoomActuel) return;

    // Ne jamais remplacer une vue agrandie ouverte sous les yeux du visiteur.
    if (zoomActuel.querySelector('.product-zoom[open]')) return;

    const nouveauZoom = documentDetache.querySelector('product-zoom');
    if (!nouveauZoom) return;

    const liste = nouveauZoom.querySelector('[data-ref="zoom-slider"] .splide__list');
    if (!liste) return;

    const diapositives = Array.from(liste.children);
    if (diapositives.length === 0) return;

    // Ici les photos sont identifiées : data-media-id est posé par le thème.
    const aGarder = diapositives.map((diapositive) => this.garder(diapositive.dataset.mediaId, couleur));
    if (!aGarder.some(Boolean)) return; // sécurité : ne jamais vider la vue agrandie

    const listeVignettes = nouveauZoom.querySelector('[data-ref="zoom-thumbnails"] .splide__list');
    const vignettes = listeVignettes ? Array.from(listeVignettes.children) : [];
    const vignettesAlignees = vignettes.length === diapositives.length;

    for (let rang = diapositives.length - 1; rang >= 0; rang -= 1) {
      if (aGarder[rang]) continue;
      diapositives[rang].remove();
      if (vignettesAlignees) vignettes[rang].remove();
    }

    // Après le remplacement du carrousel par le thème, qui est synchrone.
    requestAnimationFrame(() => {
      try {
        const cible = this.section.querySelector('product-zoom');
        if (!cible || cible.querySelector('.product-zoom[open]')) return;
        cible.replaceWith(nouveauZoom);
      } catch (erreur) {
        console.warn('[gb-galerie-couleur] vue agrandie inchangée', erreur);
      }
    });
  }
}

if (!customElements.get('gb-galerie-couleur')) {
  customElements.define('gb-galerie-couleur', GbGalerieCouleur);
}
